/**
 * Swedish (sv-SE) locale formatting utilities.
 * Centralises all number, date, time and currency formatting
 * so every UI surface follows the same Sweden standard.
 */

const LOCALE = 'sv-SE';

/* ── Numbers ─────────────────────────────────────────────── */

/** Format an integer / large number with Swedish thousand-separators (non-breaking space). */
export const formatNumber = (num) => {
  if (num === null || num === undefined || num === '') return '0';
  return Number(num).toLocaleString(LOCALE);
};

/** Format a number with a fixed number of decimals using comma as the decimal separator. */
export const formatDecimal = (num, decimals = 2) => {
  if (num === null || num === undefined) return '0';
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(num));
};

/** Format a percentage value (e.g. 0.85 → "85%", or 12.3 → "12,3%"). */
export const formatPercent = (num, decimals = 0) => {
  if (num === null || num === undefined) return '0%';
  return `${formatDecimal(num, decimals)}%`;
};

/* ── Dates ───────────────────────────────────────────────── */

/** Format a date as YYYY-MM-DD (ISO 8601 – Swedish standard). */
export const formatDate = (value) => {
  if (!value) return 'N/A';
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return String(value);
  }
};

/** Format a date with Swedish long-form month, e.g. "8 april 2026". */
export const formatDateLong = (value) => {
  if (!value) return 'N/A';
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(LOCALE, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return String(value);
  }
};

/** Format a date with short month, e.g. "8 apr. 2026". */
export const formatDateShort = (value) => {
  if (!value) return '-';
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(LOCALE, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(value);
  }
};

/* ── Time ────────────────────────────────────────────────── */

/** Format time in 24-hour format, e.g. "14:35:07". */
export const formatTime = (value) => {
  if (!value) return '';
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleTimeString(LOCALE, { hour12: false });
  } catch {
    return String(value);
  }
};

/** Full date + time, e.g. "2026-04-08 14:35". */
export const formatDateTime = (value) => {
  if (!value) return '';
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString(LOCALE, { hour12: false });
  } catch {
    return String(value);
  }
};

/* ── Relative time ───────────────────────────────────────── */

/** Swedish relative time, e.g. "för 5 minuter sedan". */
export const timeAgo = (value) => {
  if (!value) return '';
  const diff = Date.now() - new Date(value).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just nu';
  if (mins < 60) return `för ${mins} min sedan`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `för ${hrs} tim sedan`;
  const days = Math.floor(hrs / 24);
  return `för ${days} d sedan`;
};

/* ── Currency ────────────────────────────────────────────── */

/** Format a monetary value in SEK (or USD if needed). */
export const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: currency === 'USD' ? 4 : 2,
    maximumFractionDigits: currency === 'USD' ? 4 : 2,
  }).format(Number(amount));
};

/* ── String sort ─────────────────────────────────────────── */

/** Swedish-aware string comparison for sorting. */
export const svCompare = (a, b) => {
  return String(a).localeCompare(String(b), LOCALE);
};

/* ── Display date for filter inputs (YYYY-MM-DD) ─────────── */

/** Convert an input date value to YYYY-MM-DD display format (Swedish standard). */
export const formatDateForDisplay = (dateValue) => {
  if (!dateValue) return '';
  // If already in YYYY-MM-DD, return as-is (Swedish standard)
  if (dateValue.includes('-') && dateValue.split('-')[0].length === 4) {
    return dateValue;
  }
  // If in DD-MM-YYYY, convert to YYYY-MM-DD
  if (dateValue.includes('-')) {
    const [day, month, year] = dateValue.split('-');
    return `${year}-${month}-${day}`;
  }
  return dateValue;
};
